import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getFolderAndFileList } from '../Action/s3UploadApi';

// ---------------------------------------------------------------------------
// Pure helper functions (mirrors the ones in MarketingAssets component)
// ---------------------------------------------------------------------------

const normalizeFolderPath = (path) => {
  if (!path) return '';
  return path.endsWith('/') ? path : `${path}/`;
};

const normalizeS3Prefix = (prefix) => {
  if (!prefix) return '';
  const trimmed = prefix.replace(/^\/+/, '');
  return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
};

const stripS3Prefix = (path, prefix) => {
  if (!path) return '';
  const normalizedPrefix = normalizeS3Prefix(prefix);
  const cleanedPath = path.replace(/^\/+/, '');
  return normalizedPrefix && cleanedPath.startsWith(normalizedPrefix)
    ? cleanedPath.slice(normalizedPrefix.length)
    : cleanedPath;
};

const getFolderPartsFromPath = (folderPath) => {
  const trimmed = folderPath.replace(/\/+$/, '');
  const parts = trimmed.split('/').filter(Boolean);
  const folderName = parts.pop() || '';
  const parentFolderPath = parts.length ? `${parts.join('/')}/` : '';
  const fullFolderPath = folderName
    ? `${parentFolderPath}${folderName}/`
    : parentFolderPath;
  return { folderName, parentFolderPath, folderPath: fullFolderPath };
};

const resolveFileUrl = (item) => {
  const directUrl = item.url || item.s3Url || item.presignedUrl;
  if (directUrl) return directUrl;
  if (item.s3Path && /^https?:\/\//i.test(item.s3Path)) return item.s3Path;
  const publicBase = process.env.REACT_APP_S3_PUBLIC_BASE_URL;
  if (publicBase && item.s3Path) {
    const trimmedBase = publicBase.replace(/\/+$/, '');
    const trimmedPath = item.s3Path.replace(/^\/+/, '');
    return `${trimmedBase}/${trimmedPath}`;
  }
  return item.s3Path || '';
};

const getFileType = (filename) => {
  const ext = filename?.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return 'image';
  if (['mp4', 'avi', 'mov', 'wmv', 'webm'].includes(ext)) return 'video';
  return 'document';
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / k ** i) * 100) / 100 + ' ' + sizes[i];
};

// ---------------------------------------------------------------------------
// Async thunk — fetches and maps folders + files in one call
// ---------------------------------------------------------------------------

export const fetchMarketingAssetsData = createAsyncThunk(
  'marketingAssets/fetchData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getFolderAndFileList();
      const responseData = response?.data || {};
      const s3Prefix = normalizeS3Prefix(responseData?.s3Prefix);
      const rawFolders = Array.isArray(responseData?.folders) ? responseData.folders : [];
      const rawFiles = Array.isArray(responseData?.files) ? responseData.files : [];

      const mappedFolders = rawFolders.map((item) => {
        const relativePath = stripS3Prefix(item.s3Path || item.name || '', s3Prefix);
        const normalizedPath = normalizeFolderPath(relativePath || item.name || '');
        const { folderName, parentFolderPath, folderPath } = getFolderPartsFromPath(normalizedPath);
        return {
          id: item.folderId || item.s3Path || folderPath,
          folderName,
          parentFolderPath,
          folderPath,
          s3Path: item.s3Path,
          createdAt: item.createdAt,
          lastModified: item.lastModified,
        };
      });

      const mappedFiles = rawFiles.map((item) => {
        const relativePathFromS3 = stripS3Prefix(item.s3Path || '', s3Prefix);
        const fallbackPath = `${item.path || ''}${item.name || ''}`;
        const relativePath = relativePathFromS3 || fallbackPath || item.name || '';
        const normalizedPath = relativePath.replace(/^\/+/, '');
        const parts = normalizedPath.split('/').filter(Boolean);
        const fileName = item.name || parts.pop() || '';
        const parentFolderPath = parts.length ? `${parts.join('/')}/` : '';
        const normalizedFilePath =
          item.path !== undefined && item.path !== null ? normalizeFolderPath(item.path) : '';
        const effectiveFolderPath =
          item.path !== undefined && item.path !== null ? normalizedFilePath : parentFolderPath;
        const extension = fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : '';
        return {
          id: item.s3Path || `${parentFolderPath}${fileName}`,
          name: fileName,
          type: getFileType(fileName),
          url: resolveFileUrl(item),
          size: formatFileSize(item.size),
          sizeBytes: item.size || 0,
          uploadDate: item.createdAt || item.lastModified || '',
          tags: [],
          description: '',
          format: extension ? extension.toUpperCase() : 'FILE',
          dimensions: '',
          duration: '',
          pages: null,
          folderPath: effectiveFolderPath,
          s3Path: item.s3Path,
        };
      });

      return { folders: mappedFolders, assets: mappedFiles };
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to fetch marketing assets');
    }
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const marketingAssetsSlice = createSlice({
  name: 'marketingAssets',
  initialState: {
    folders: [],
    assets: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    lastFetchedAt: null,
  },
  reducers: {
    /** Set status back to idle so the next mount re-fetches (e.g. after hard reload button). */
    invalidateMarketingAssets: (state) => {
      state.status = 'idle';
      state.lastFetchedAt = null;
    },

    // --- Asset mutations (optimistic / in-place) ---

    addAssetToStore: (state, action) => {
      state.assets.unshift(action.payload);
    },
    updateAssetInStore: (state, action) => {
      const idx = state.assets.findIndex((a) => a.id === action.payload.id);
      if (idx !== -1) {
        state.assets[idx] = { ...state.assets[idx], ...action.payload };
      }
    },
    removeAssetFromStore: (state, action) => {
      state.assets = state.assets.filter((a) => a.id !== action.payload);
    },

    // --- Folder mutations (optimistic / in-place) ---

    addFolderToStore: (state, action) => {
      state.folders.unshift(action.payload);
    },
    updateFolderInStore: (state, action) => {
      const idx = state.folders.findIndex((f) => f.id === action.payload.id);
      if (idx !== -1) {
        state.folders[idx] = { ...state.folders[idx], ...action.payload };
      }
    },
    removeFolderFromStore: (state, action) => {
      state.folders = state.folders.filter((f) => f.id !== action.payload);
    },

    /**
     * Handles the rename-cascade when a folder is renamed — updates the renamed
     * folder and all descendant folders whose paths start with the old path.
     */
    renameFolderCascade: (state, action) => {
      const { folderId, oldPath, newPath, newFolderName } = action.payload;
      state.folders = state.folders.map((folder) => {
        if (folder.id === folderId) {
          return { ...folder, folderName: newFolderName, folderPath: newPath };
        }
        const normalizedParent = normalizeFolderPath(folder.parentFolderPath);
        if (normalizedParent === oldPath) {
          return {
            ...folder,
            parentFolderPath: newPath,
            folderPath: `${newPath}${folder.folderName}/`,
          };
        }
        if (folder.folderPath?.startsWith(oldPath)) {
          return {
            ...folder,
            parentFolderPath: normalizeFolderPath(folder.parentFolderPath).replace(oldPath, newPath),
            folderPath: folder.folderPath.replace(oldPath, newPath),
          };
        }
        return folder;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMarketingAssetsData.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchMarketingAssetsData.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.folders = action.payload.folders;
        state.assets = action.payload.assets;
        state.lastFetchedAt = Date.now();
        state.error = null;
      })
      .addCase(fetchMarketingAssetsData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch marketing assets';
      });
  },
});

export const {
  invalidateMarketingAssets,
  addAssetToStore,
  updateAssetInStore,
  removeAssetFromStore,
  addFolderToStore,
  updateFolderInStore,
  removeFolderFromStore,
  renameFolderCascade,
} = marketingAssetsSlice.actions;

export default marketingAssetsSlice.reducer;
